import moment from 'moment-timezone';

// Business rule: an installation that names no time zone is shown in this zone, and the page says
// so. The zone is the one the platform aggregates telemetry in when it knows no better.
export const FALLBACK_TIME_ZONE = 'Europe/Amsterdam';

export interface DisplayZone {
  zone: string;
  // True when the fallback stands in: no installation named a zone, or they named different ones.
  fallback: boolean;
}

// The zone to show times in: the one zone the installations name, else the fallback. A device
// feeds one installation at a time, so several different zones mean the answer is not trustworthy.
export function displayZone(timezones: readonly (string | null | undefined)[]): DisplayZone {
  const named = [
    ...new Set(timezones.filter((zone): zone is string => !!zone && moment.tz.zone(zone) !== null)),
  ];
  return named.length === 1
    ? { zone: named[0], fallback: false }
    : { zone: FALLBACK_TIME_ZONE, fallback: true };
}

function utcOffset(local: moment.Moment): string {
  return `UTC${local.format('Z')}`;
}

// The zone label of an instant on the installation's clock, e.g. CET or CEST in Europe/Amsterdam.
// Many zones have no abbreviation of their own (moment shows "+08" in Asia/Singapore): those get
// their UTC offset instead.
export function zoneLabel(instant: moment.MomentInput, zone: string): string {
  const local = moment.tz(instant, zone);
  const abbreviation = local.format('z');
  return /^[A-Za-z]+$/.test(abbreviation) ? abbreviation : utcOffset(local);
}

// Every label the zone uses in a year with its UTC offset, e.g. "CET UTC+01:00, CEST UTC+02:00" in
// Europe/Amsterdam, "IST UTC+05:30" in India and "UTC+08:00" in Singapore.
export function zoneLabels(zone: string, year: number): string {
  const labels = [0, 6].map(month => {
    const local = moment.tz(Date.UTC(year, month, 1), zone);
    const label = zoneLabel(local, zone);
    const offset = utcOffset(local);
    return offset.startsWith(label) ? offset : `${label} ${offset}`;
  });
  return [...new Set(labels)].join(', ');
}

// An instant on the installation's clock, always with its zone label.
export function formatInZone(instant: moment.MomentInput, zone: string, format: string): string {
  return `${moment.tz(instant, zone).format(format)} ${zoneLabel(instant, zone)}`;
}

// The ISO week of a day, with the ISO week-year: the week that straddles New Year belongs to the
// year of its Thursday, so 2024-12-30 is week 1 of 2025.
export function isoWeekOf(day: moment.Moment): { year: number; week: number } {
  return { year: day.isoWeekYear(), week: day.isoWeek() };
}
